'use client'

/**
 * Shared client-side upload path for user file attachments (brand-setup
 * form + chat composer).
 *
 * Images are normalized to a canonical format (static → PNG, animated →
 * GIF). Two shapes:
 *   - SVG: read as raw text and POST straight to the normalize route.
 *     SVG is the one image type a browser executes when served inline, so
 *     it is NEVER staged on Blob — it is rasterized server-side from text.
 *   - Other images: `/api/upload` token flow under `uploads/originals/`
 *     (Vercel caps serverless bodies at ~4.5MB). Attachment mode then
 *     hands the URL to normalize. Brand-import mode skips that round trip
 *     for animated files and anything over 4 MiB (`shouldStageOriginalFirst`)
 *     and lets the import drainer convert.
 * Non-image files (PDF, DOCX, CSV, text) upload once, unchanged, to the
 * PRIVATE Blob store through `/api/chat-files/upload`, under the org and
 * brand the caller passes as `chatFileScope` (`upload-private-chat-file.ts`,
 * loaded only when a document is attached): a public URL is readable by
 * anyone who has it, and these are the customer's documents. The server
 * reads them back for the model (`lib/shared/blob/private-chat-files.ts`).
 * PDFs included: no browser ever opens a private file, so a PDF's JavaScript
 * never runs from our origin (the sanitizer `/api/upload/sanitize-pdf` is not
 * on this path).
 *
 * Every failure is a typed `UploadError` (`./upload-error.ts`) that carries
 * the server's message and whether a retry can help. Attachment conversion
 * verdicts are never re-run through another path — see `stageConvertibleImage`.
 */

import {
  DIRECT_NORMALIZE_MAX_BYTES,
  guessMediaTypeFromFilename,
  isConvertibleImageMediaType,
  isSvgMediaType,
  MAX_SVG_INLINE_BYTES,
  MULTIPART_THRESHOLD_BYTES,
  shouldStageOriginalFirst,
  UPLOAD_FALLBACKS_PREFIX,
  UPLOAD_ORIGINALS_PREFIX,
} from '@/lib/core/media/image-upload-support'
import type { ChatFileScope } from '@/lib/shared/blob/chat-file-paths'
import { isTransientBrewUploadUrl } from '@/lib/shared/blob/owned-blob-url'
import { toCdnUrl } from '@/lib/site/cdn'
import { measureImageFile } from './measure-image-file'
import { findAlreadyUploaded } from './upload-content-dedupe'
import {
  UploadError,
  uploadErrorFromFetchFailure,
  uploadErrorFromResponse,
} from './upload-error'
import { blobStageFailure, withStagingRetry } from './upload-staging'
import type { UploadedUserFile } from './uploaded-user-file'

export { UploadError } from './upload-error'
export type { UploadedUserFile } from './uploaded-user-file'

/**
 * Who is going to read the result decides how much the browser waits for.
 *
 *   - `attachment` (default; chat, editor, image-to-email): the URL goes
 *     straight into a message or an `<Img src>`, so it must be the durable
 *     normalized asset — conversion runs inline, and an already-safe format
 *     may fall back to a durable copy of the original if conversion is down.
 *   - `brand-import` (the brand library import queue ONLY): its drainer
 *     normalizes server-side. Heavy and animated files take the FAST lane —
 *     the browser only waits for the CDN put and hands over the staged
 *     original — and nothing ever falls back to `uploads/fallbacks/`,
 *     because the queue accepts only our own `uploads/normalized/` or
 *     `uploads/originals/` URLs. A staged original is deleted 24h after
 *     upload: it is a handoff to the drainer, never content, so every
 *     other consumer uses `attachment` (enforced in `uploadUserFile`).
 */
export type UploadUserFileMode = 'attachment' | 'brand-import'

export type UploadUserFileOptions = {
  mode?: UploadUserFileMode
  /**
   * Whose documents these are. Required for a non-image file: it names the
   * private store path the upload is authorized for. Images ignore it.
   */
  chatFileScope?: ChatFileScope
}

/**
 * Deadline for one normalize round trip.
 *
 * `fetch` has no default timeout, so a black-holed request here hangs one of
 * the staging pool's workers forever — the pool shrinks by one with no error to
 * learn from, and a 449-file upload can end up wedged on a handful of dead
 * sockets. MUST stay above the server's own conversion budget
 * (`NORMALIZE_SERVER_BUDGET_MS`, 60s): the server's 504 verdict is what tells
 * this client a GIF cannot be converted; a client that gives up first only
 * re-runs the same doomed conversion through the next path.
 */
const NORMALIZE_TIMEOUT_MS = 120_000

/** Formats that are already safe end-to-end if conversion is unavailable. */
const CONVERSION_FALLBACK_SAFE = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
])

type NormalizeRequest =
  /** Bytes posted directly — no Blob staging (≤ DIRECT_NORMALIZE_MAX_BYTES). */
  | { kind: 'direct'; file: File; filename: string; mediaType: string }
  /** Raw SVG text, rasterized server-side. */
  | { kind: 'svg'; svgSource: string; filename: string }
  /** A staged `uploads/originals/` blob URL. */
  | { kind: 'staged'; url: string; filename: string; mediaType: string }

function normalizeRequestInit(request: NormalizeRequest): RequestInit {
  if (request.kind === 'direct') {
    const form = new FormData()
    form.append('file', request.file)
    form.append('filename', request.filename)
    form.append('mediaType', request.mediaType)
    return { method: 'POST', body: form }
  }
  const body =
    request.kind === 'svg'
      ? { svgSource: request.svgSource, filename: request.filename }
      : {
          url: request.url,
          mediaType: request.mediaType,
          filename: request.filename,
        }
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }
}

/**
 * ONE round trip to `/api/upload/normalize-image` for all three request
 * shapes. Never returns null: a failure is a typed `UploadError` carrying
 * the server's message and verdict, so the caller decides the next step from
 * the CODE (fall back, retry, or stop) rather than guessing from an absence.
 */
async function requestNormalize(
  request: NormalizeRequest
): Promise<UploadedUserFile> {
  let response: Response
  try {
    response = await fetch('/api/upload/normalize-image', {
      ...normalizeRequestInit(request),
      signal: AbortSignal.timeout(NORMALIZE_TIMEOUT_MS),
    })
  } catch (error) {
    throw uploadErrorFromFetchFailure(error, request.filename)
  }
  if (!response.ok) {
    throw await uploadErrorFromResponse(
      response,
      `Could not process "${request.filename}" — try PNG or JPEG`
    )
  }
  const converted = (await response.json()) as UploadedUserFile
  return {
    url: converted.url,
    mediaType: converted.mediaType,
    filename: converted.filename,
    ...(typeof converted.width === 'number' ? { width: converted.width } : {}),
    ...(typeof converted.height === 'number'
      ? { height: converted.height }
      : {}),
  }
}

/**
 * Whether a DIRECT-path failure should be retried through the staged path.
 * Only when staging changes something: the bytes were over the direct body
 * ceiling, or the single 4MB POST itself could not get through (Blob multipart
 * with per-part retry is a genuinely different transport). A conversion
 * verdict is the same on both paths and is never re-run.
 */
function shouldFallBackToStaged(error: unknown): boolean {
  return (
    error instanceof UploadError &&
    (error.code === 'too_large' || error.code === 'network')
  )
}

/**
 * Whether a STAGED-path conversion failure may hand the customer their
 * original bytes instead. Never for `unsupported_image` — a file sharp cannot
 * decode is not an image the product can render either.
 */
function mayFallBackToOriginal(error: unknown): boolean {
  return (
    error instanceof UploadError &&
    (error.code === 'conversion_timeout' ||
      error.code === 'conversion_failed' ||
      error.code === 'server_unavailable' ||
      error.code === 'network')
  )
}

/**
 * Stamp the customer's own file name onto whatever the staging path returned.
 *
 * Done in ONE place rather than at each `return` inside `stageUserFile`: that
 * function has four exit points across three normalization paths, and a name
 * missing from any one of them silently degrades dedupe back to the truncated
 * stored name for exactly the files that took that path.
 */
export async function uploadUserFile(
  file: File,
  /**
   * `undefined` means this single-file caller still needs a lookup. Bulk
   * callers pass either the known hit or `null` after one deterministic batch
   * lookup, so they never fall back to one HTTP request per file.
   */
  contentLookup?: UploadedUserFile | null,
  options: UploadUserFileOptions = {}
): Promise<UploadedUserFile> {
  const staged = await stageUserFile(file, contentLookup, options)
  // Every surface hands out the CDN host, whatever path produced the URL.
  const url = toCdnUrl(staged.url)
  // Attachment consumers write this URL straight into a message or an
  // `<Img src>`. A staged original there is an image that 404s in 24 hours
  // (retention) — the class of bug that broke sent campaigns — so only the
  // brand-import lane, whose drainer converts it, may ever receive one.
  if (options.mode !== 'brand-import' && isTransientBrewUploadUrl(url)) {
    throw new UploadError(
      'rejected',
      `Could not finish uploading "${file.name || staged.filename}" — try again`,
      { retryable: false }
    )
  }
  return { ...staged, url, originalFilename: file.name || staged.filename }
}

async function stageUserFile(
  file: File,
  contentLookup?: UploadedUserFile | null,
  options: UploadUserFileOptions = {}
): Promise<UploadedUserFile> {
  const filename = file.name || `file-${Date.now()}`
  // Browsers leave `File.type` empty for many extensions (.md/.yaml on
  // macOS) and the blob token route rejects unknown content types, so
  // always resolve an explicit one.
  const contentType = (
    file.type ||
    guessMediaTypeFromFilename(file.name) ||
    'application/octet-stream'
  ).toLowerCase()

  // SVG → rasterize from raw text; never touches Blob as an svg blob.
  if (isSvgMediaType(contentType)) {
    if (file.size > MAX_SVG_INLINE_BYTES) {
      throw new UploadError(
        'too_large',
        `"${filename}" is too large to process as an SVG`
      )
    }
    const svgSource = await file.text()
    return withStagingRetry(() =>
      requestNormalize({ kind: 'svg', svgSource, filename })
    )
  }

  if (isConvertibleImageMediaType(contentType)) {
    return stageConvertibleImage(
      file,
      filename,
      contentType,
      contentLookup,
      options
    )
  }

  // Non-image file — single upload, no conversion, to the private store. Its
  // code loads only when a document is attached, never with the composer.
  const { uploadPrivateChatFile } = await import('./upload-private-chat-file')
  const result = await uploadPrivateChatFile(
    file,
    filename,
    options.chatFileScope
  )
  return { url: result.url, mediaType: result.mediaType, filename }
}

/** Client-token upload of an image to the PUBLIC store, retried on transport blips. */
function stageOriginalOnBlob(
  pathname: string,
  file: File,
  contentType: string
): Promise<{ url: string }> {
  return withStagingRetry(async () => {
    try {
      // Loaded on demand, like `stagePrivateChatFile` (client bundle size).
      const { upload } = await import('@vercel/blob/client')
      return await upload(pathname, file, {
        access: 'public',
        handleUploadUrl: '/api/upload',
        contentType,
        // Large originals stream in parts instead of one long-lived request,
        // so a stall costs a part rather than the whole file.
        multipart: file.size > MULTIPART_THRESHOLD_BYTES,
      })
    } catch (error) {
      throw blobStageFailure(error, file, pathname)
    }
  })
}

/**
 * The image pipeline, as an explicit decision table. Each step either
 * returns a normalized file or throws a typed `UploadError`; the only
 * transitions between steps are the ones where the next step can actually
 * change the outcome.
 *
 *   dedupe hit          → done
 *   brand-import + heavy/animated
 *                       → stage original to CDN → done (server normalizes)
 *   direct normalize    → done | too_large/network → staged | verdict → throw
 *   staged normalize    → done | timeout/5xx/network → original copy (when
 *                         allowed) | unsupported → throw
 */
async function stageConvertibleImage(
  file: File,
  filename: string,
  contentType: string,
  contentLookup: UploadedUserFile | null | undefined,
  options: UploadUserFileOptions
): Promise<UploadedUserFile> {
  // FASTEST PATH: we already hold these exact bytes.
  //
  // Re-picking a folder is the most common bulk-upload gesture there is, and
  // every unchanged file in it used to be uploaded, Sharp-decoded, re-encoded
  // and stored a second time. Hashing costs one extra read of the file and a
  // batched round trip; a hit saves the entire pipeline. Any failure inside
  // returns null and we stage normally, so this can only ever be a shortcut.
  const known =
    contentLookup === undefined
      ? await findAlreadyUploaded(file)
      : contentLookup
  if (known) {
    return known
  }

  // FAST LANE: the consumer normalizes server-side, so a heavy or animated
  // file is done the moment its bytes are on the CDN. This is the whole fix
  // for "the GIF upload never finishes" — the browser no longer waits on a
  // frame-by-frame encode, and the conversion happens in the import drainer
  // where nobody is watching a spinner. Dimensions are decoded locally as a
  // hint so the card can reserve its aspect ratio.
  if (options.mode === 'brand-import' && shouldStageOriginalFirst(file)) {
    const [staged, measured] = await Promise.all([
      stageOriginalOnBlob(
        `${UPLOAD_ORIGINALS_PREFIX}/${filename}`,
        file,
        contentType
      ),
      measureImageFile(file),
    ])
    return {
      url: toCdnUrl(staged.url),
      mediaType: contentType,
      filename,
      ...(measured ? { width: measured.width, height: measured.height } : {}),
      isStagedOriginal: true,
    }
  }

  // FAST PATH: small enough to post the bytes directly — no Blob staging, no
  // server-side re-download, no cleanup delete. The staged path exists only
  // because Vercel caps serverless request bodies at ~4.5MB.
  if (file.size <= DIRECT_NORMALIZE_MAX_BYTES) {
    try {
      return await withStagingRetry(() =>
        requestNormalize({
          kind: 'direct',
          file,
          filename,
          mediaType: contentType,
        })
      )
    } catch (error) {
      // A conversion verdict (422 unsupported, 504 out of time) is the SAME on
      // the staged path — re-running it there is what used to turn one slow
      // GIF into four server encodes. Only a transport problem falls through.
      if (!shouldFallBackToStaged(error)) {
        throw error
      }
    }
  }

  // Stage the original under the transient originals prefix so the normalize
  // route can safely delete it after conversion.
  const staged = await stageOriginalOnBlob(
    `${UPLOAD_ORIGINALS_PREFIX}/${filename}`,
    file,
    contentType
  )
  try {
    return await withStagingRetry(() =>
      requestNormalize({
        kind: 'staged',
        url: staged.url,
        filename,
        mediaType: contentType,
      })
    )
  } catch (error) {
    // Never silently forward formats the downstream pipeline can't handle
    // (raw AVIF/WebP/TIFF would 400 the model or vanish from the library), and
    // never forward bytes sharp could not decode at all.
    if (
      !(
        mayFallBackToOriginal(error) &&
        CONVERSION_FALLBACK_SAFE.has(contentType)
      )
    ) {
      throw error
    }
    // Brand-library queues accept only our own normalized or staged URLs —
    // never a fallback copy — so the customer gets the real verdict (a 504
    // says "shorten the GIF", which a generic "try again" never would).
    if (options.mode === 'brand-import') {
      throw error
    }
    // The `uploads/originals/` URL is TRANSIENT and retention deletes it after
    // 24h. Returning it here used to create live email/chat images with a
    // one-day expiry. Copy the customer's File into a durable random-suffixed
    // key and leave the staged original for bounded garbage collection.
    const fallback = await stageOriginalOnBlob(
      `${UPLOAD_FALLBACKS_PREFIX}/${filename}`,
      file,
      contentType
    )
    return { url: toCdnUrl(fallback.url), mediaType: contentType, filename }
  }
}
